import React, { useEffect, useState } from 'react';
import { Avatar } from '@mui/material';
import { Apps as AppsIcon } from '@mui/icons-material';
import apiService from '../../services/api';

/**
 * Avatar for a custom application's uploaded icon.
 *
 * The icon endpoint is authenticated and served from API_BASE_URL, which is a different origin
 * than the admin UI. An <img> can neither send the Authorization: Bearer header nor rely on the
 * auth_token cookie (host-only, so it is not sent cross-origin), so the bytes are pulled through
 * the authenticated API client and handed to the Avatar as an object URL.
 *
 * Most applications have no icon, so every non-success path — no icon, still loading, request
 * failed — falls back silently to `children`. Nothing here surfaces an error to the user.
 *
 * `version` is what makes a REPLACED icon appear. Replacing one changes neither id and leaves
 * hasIcon true either side, so without it the post-save refetch reconciles this component
 * rather than remounting it and the old object URL survives. Pass the application's
 * `updatedAt`, which the backend bumps on an icon write; every call site does.
 *
 * Two layers are needed, because the version defeats two different caches:
 *   - React's memoisation, via the effect dependency here (and `key` at the call sites);
 *   - the BROWSER's HTTP cache, because the endpoint answers with
 *     `Cache-Control: public, max-age=3600`. `version` is forwarded to
 *     api.js's getCustomApplicationIconBlob, which appends it as a `?v=` query parameter so
 *     the URL itself changes when the bytes do.
 *
 * Dropping either layer silently reintroduces the bug: the refetch would be skipped entirely,
 * or issued and then answered from cache with the old image. Neither fails loudly.
 */
const ApplicationIcon = ({
  customerId,
  applicationId,
  hasIcon,
  version,
  children = <AppsIcon />,
  ...avatarProps
}) => {
  const [iconUrl, setIconUrl] = useState(null);

  useEffect(() => {
    // Drop the previous application's icon before loading the next one, so a re-keyed
    // instance never shows the wrong image while its fetch is in flight.
    setIconUrl(null);

    if (!hasIcon || !customerId || !applicationId) {
      return undefined;
    }

    // `cancelled` covers unmount and mid-flight source changes: a stale response must not
    // create an object URL that nothing is left to revoke.
    let cancelled = false;
    let createdUrl = null;

    apiService
      .getCustomApplicationIconBlob(customerId, applicationId, { version })
      .then((blob) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setIconUrl(createdUrl);
      })
      .catch(() => {
        // A missing or unreadable icon is a normal state, not an error worth reporting.
      });

    return () => {
      cancelled = true;
      // Object URLs live for the lifetime of the document until revoked, and this component
      // renders once per row in the application list.
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [customerId, applicationId, hasIcon, version]);

  return (
    <Avatar src={iconUrl || undefined} {...avatarProps}>
      {children}
    </Avatar>
  );
};

export default ApplicationIcon;
